import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import TrashCanIcon from '@/assets/icons/trash.svg?react';
import { DocumentPreviewWidget } from '@/components/molecules/DocumentPreviewWidget';
import { useTranslator } from '@/hooks/useTranslator';
import { FileInterface } from '@/types/fileInterface';

interface Props {
  readonly name?: string;

  readonly value: FileInterface | null;

  readonly error: string | null;
  readonly status: QueryStatus;

  onChange(event: React.ChangeEvent<HTMLInputElement>): void;
  onRemove(): void;
}

export const FileInput: React.FC<Props> = (props: Props): React.ReactElement => {
  const { name, value, error, status, onChange, onRemove } = props;

  const [fileInput, setFileInput] = React.useState<HTMLInputElement | null>(null);
  const tr = useTranslator();

  const onClick = React.useCallback((): void => {
    fileInput?.click();
  }, [fileInput]);

  const deleteFile = React.useCallback((): void => {
    onRemove?.();
  }, [onRemove]);

  return (
    <Box sx={styles.container}>
      <input ref={setFileInput} type="file" style={fileInputStyle} onChange={onChange} />
      {status === QueryStatus.pending ? (
        <DocumentPreviewWidget
          name=""
          icon={TrashCanIcon}
          size={null}
          status={QueryStatus.pending}
        />
      ) : value ? (
        <DocumentPreviewWidget
          name={value.name}
          icon={TrashCanIcon}
          size={value.size}
          status={status}
          error={error}
          onIconClick={deleteFile}
        />
      ) : (
        <ButtonBase id={name} sx={styles.button} onClick={onClick}>
          <Typography fontSize={16} fontWeight={400} color="text.primary" lineHeight="56px">
            {tr('UPLOAD_DOCUMENT')}
          </Typography>
        </ButtonBase>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  currentFileBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'custom.OffWhite',
    borderColor: 'custom.DividerMed',
    borderWidth: 1,
    borderStyle: 'solid',
    py: 2,
    pl: 3,
    pr: 2,
    borderRadius: 4,
    position: 'relative',
  },

  button: {
    width: '100%',
    color: 'custom.DividerLight',
    height: 56,
    borderRadius: 28,
    borderWidth: 1,
    borderColor: 'custom.DividerLight',
    borderStyle: 'solid',

    '&:focus': {
      borderColor: 'text.primary',
    },
  },
};

const fileInputStyle: React.CSSProperties = { display: 'none' };
